// Customer create/edit dialog
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { FileText, MessageSquare } from 'lucide-react'
import { useCustomers } from '@/hooks/useCustomers'
import { CustomerProfilerChat } from './CustomerProfilerChat'

interface CustomerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customerId: string | null
}

export function CustomerDialog({ open, onOpenChange, customerId }: CustomerDialogProps) {
  const { customers, createCustomer, updateCustomer } = useCustomers()
  const [name, setName] = useState('')
  const [companyCode, setCompanyCode] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [description, setDescription] = useState('')
  const [configNotes, setConfigNotes] = useState('')
  const [agentInstructions, setAgentInstructions] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (customerId) {
      const customer = customers.find((c) => c.id === customerId)
      if (customer) {
        setName(customer.name)
        setCompanyCode(customer.company_code || '')
        setEmail(customer.contact_email || '')
        setPhone(customer.contact_phone || '')
        setDescription(customer.description || '')
        setConfigNotes(customer.configuration_notes || '')
        setAgentInstructions(customer.agent_instructions || '')
      }
    } else {
      setName('')
      setCompanyCode('')
      setEmail('')
      setPhone('')
      setDescription('')
      setConfigNotes('')
      setAgentInstructions('')
    }
  }, [customerId, customers, open])

  const handleProfileGenerated = (profile: {
    description: string
    configuration_notes: string
    agent_instructions: string
  }) => {
    setDescription(profile.description)
    setConfigNotes(profile.configuration_notes)
    setAgentInstructions(profile.agent_instructions)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (customerId) {
        const data = {
          name,
          company_code: companyCode || null,
          contact_email: email || null,
          contact_phone: phone || null,
          description: description || null,
          configuration_notes: configNotes || null,
          agent_instructions: agentInstructions || null,
        }
        await updateCustomer(customerId, data)
      } else {
        const data = {
          name,
          company_code: companyCode || null,
          contact_email: email || null,
          contact_phone: phone || null,
          description: description || null,
          configuration_notes: configNotes || null,
          agent_instructions: agentInstructions || null,
        }
        await createCustomer(data)
      }
      onOpenChange(false)
    } catch (err) {
      alert('Failed to save customer: ' + (err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => onOpenChange(false)}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-lg font-semibold">{customerId ? 'Edit' : 'Add'} Customer</h2>

        <Tabs defaultValue="form" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="form" className="gap-2">
              <FileText className="h-4 w-4" />
              Profile Form
            </TabsTrigger>
            <TabsTrigger value="chat" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Chat with AI Profiler
            </TabsTrigger>
          </TabsList>

          <TabsContent value="form">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Customer Name *</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="code">Company Code</Label>
                <Input id="code" value={companyCode} onChange={(e) => setCompanyCode(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="email">Contact Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="phone">Contact Phone</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="General description of the customer..."
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="config">Configuration Notes</Label>
                <Textarea
                  id="config"
                  value={configNotes}
                  onChange={(e) => setConfigNotes(e.target.value)}
                  placeholder="Technical configuration details, customizations, etc..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="instructions">Agent Instructions</Label>
                <Textarea
                  id="instructions"
                  value={agentInstructions}
                  onChange={(e) => setAgentInstructions(e.target.value)}
                  placeholder="Special instructions for AI agents when handling this customer..."
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="chat" className="mt-4">
            <CustomerProfilerChat
              customerId={customerId || undefined}
              onProfileGenerated={handleProfileGenerated}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={saving || !name.trim()}
                className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
              >
                {saving ? 'Saving...' : 'Save Customer'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
